import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ExternalLink, Package, ShoppingCart } from "lucide-react";
import { BundleItemCard } from "@/components/bundles/BundleItemCard";
import { useBundle } from "@/hooks/useBundles";
import { incrementBundleViews, logBundleClick } from "@/data/bundles";
import { generateBuyAllUrls } from "@/lib/affiliateLinks";
import { toast } from "sonner";

export default function GearBundleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const { data: bundle, isLoading, error } = useBundle(slug || "");

  // Track view on mount
  useEffect(() => {
    if (bundle?.id) {
      incrementBundleViews(bundle.id).catch(console.error);
    }
  }, [bundle?.id]);

  const handleBuyAll = async () => {
    if (!bundle) return;

    try {
      // Log the click
      await logBundleClick(bundle.id, null, {
        user_agent: navigator.userAgent,
        referrer: document.referrer,
      });

      // Generate URLs for all items
      const urls = generateBuyAllUrls(
        bundle.retailer.slug,
        bundle.items.map(item => ({
          search_query: item.search_query,
          name: item.name,
        }))
      );

      // Open all URLs in new tabs
      urls.forEach(({ url }) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      });

      toast.success(`Opening ${urls.length} items on ${bundle.retailer.name}`);
    } catch (error) {
      console.error('Failed to track bundle click:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  const handleItemClick = async (itemId: string) => {
    if (!bundle) return;

    try {
      await logBundleClick(bundle.id, itemId, {
        user_agent: navigator.userAgent,
        referrer: document.referrer,
      });
    } catch (error) {
      console.error('Failed to track item click:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center py-12">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Bundle Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The bundle you're looking for doesn't exist or has been removed.
            </p>
            <Button onClick={() => navigate('/gearbundles')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Bundles
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const priceRange = bundle.total_price_min && bundle.total_price_max
    ? `$${bundle.total_price_min}–$${bundle.total_price_max}`
    : 'Price varies';

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link
              to="/gearbundles"
              className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Bundles
            </Link>
          </div>

          {/* Hero Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left - Image/Logo */}
            <div className="lg:col-span-1">
              <Card>
                <CardContent className="p-6">
                  <div className="aspect-square bg-muted rounded-lg flex items-center justify-center mb-4">
                    {bundle.retailer.logo_url ? (
                      <img
                        src={bundle.retailer.logo_url}
                        alt={bundle.retailer.name}
                        className="max-w-[80%] max-h-[80%] object-contain"
                      />
                    ) : (
                      <Package className="h-24 w-24 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-sm text-center text-muted-foreground">
                    Available at {bundle.retailer.name}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Right - Details */}
            <div className="lg:col-span-2 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge
                    variant="secondary"
                    style={{
                      backgroundColor: bundle.version.badge_color || undefined,
                    }}
                  >
                    {bundle.version.name}
                  </Badge>
                </div>
                <h1 className="text-3xl font-bold mb-2">{bundle.kit_type.name}</h1>
                <p className="text-muted-foreground">{bundle.summary}</p>
              </div>

              <div className="flex items-center gap-6 text-sm">
                <div>
                  <span className="text-2xl font-bold">{priceRange}</span>
                </div>
                <Separator orientation="vertical" className="h-8" />
                <div className="text-muted-foreground">
                  {bundle.item_count} items
                </div>
                <Separator orientation="vertical" className="h-8" />
                <div className="text-muted-foreground">
                  {bundle.views_count || 0} views
                </div>
              </div>

              <div className="pt-4">
                <Button
                  size="lg"
                  className="w-full lg:w-auto"
                  onClick={handleBuyAll}
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Buy All Items on {bundle.retailer.name}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Opens search results for each item in new tabs
                </p>
              </div>
            </div>
          </div>

          {/* Items List */}
          <Card>
            <CardHeader>
              <CardTitle>What's Included</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {bundle.items
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((item) => (
                  <BundleItemCard
                    key={item.id}
                    item={item}
                    retailerSlug={bundle.retailer.slug}
                    retailerName={bundle.retailer.name}
                    onItemClick={handleItemClick}
                  />
                ))}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}
